import * as Sentry from '@sentry/react';
import { NvTopbar, useNvAlert } from '@simwood/nv-react-components';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import changelogContent from '../../CHANGELOG.md?raw';
import { isWeb } from '../../shared/helpers/platform';
import * as platform from '../../shared/helpers/platformApi';
import { handleEnterPress, isVcrmSupportedOs } from '../../shared/helpers/util';
import { fetchOutgoingCallerIds } from '../../shared/reducers/client';
import { fetchContacts } from '../../shared/reducers/contacts';
import { fetchSmSMessages } from '../../shared/reducers/sms';
import { fetchTimeIntervals } from '../../shared/reducers/timeIntervals';
import { RemoteAudio, UserAgentAudio } from '../JsSIP/interfaces';
import AudioDeviceModal from '../components/AudioDeviceModal';
import CenterColumn from '../components/CenterColumn';
import ContactList from '../components/ContactList';
import ExtensionSelect from '../components/ExtensionSelect';
import { logActivity } from '../helpers/activityLog';
import { shouldLogAudioDiagnostics } from '../helpers/audioDiagnostics';
import {
  getRemoteAudio,
  getRemoteAudioElement,
  setRemoteOutputDeviceId,
} from '../helpers/remoteAudio';
import {
  getAudioDevices,
  testCanChangeAudioDevice,
} from '../helpers/getAudioDevices';
import * as partnerCrm from '../helpers/partnerCrm';
import {
  getInboundRinging,
  getOutboundRinging,
  peekInboundRinging,
  peekOutboundRinging,
  setAudioDiagnosticsEnabled,
} from '../helpers/ringing';
import { grantAudio } from '../reducers/audio';
import { fetchCallHistory, fetchNewCalls } from '../reducers/callHistory';
import { registerExtension } from '../reducers/extension';
import { logout } from '../reducers/user';
import { fetchLaunchToken, fetchLicences } from '../reducers/vcrm';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/client.module.scss';
import ChangelogModal from './ChangelogModal';
import ExtensionSettingsModal from './ExtensionSettingsModal';
import ForwardingRulesModal from './ForwardingRulesModal';
import Hammer from './Hammer';
import Popover from './Popover';
import RightPane from './RightPane';
import VcrmIndicator from './VcrmIndicator';
import VoicemailSettingsModal from './VoicemailSettingsModal';
import { ChevronLeftIcon, ChevronRightIcon, ClearIcon } from './icons';

// TODO move
platform.init();

const Client: React.FC = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const visibleExtensionMenu = useSelector(
    (state: StoreState) => state.client.visibleExtensionMenu,
  );
  const newOutboundCall = useSelector(
    (state: StoreState) =>
      !!state.calls.find((c) => c.direction === 'OUT' && c.ringing),
  );
  const isRegistered = useSelector(
    (state: StoreState) => state.extension.status === 'registered',
  );
  const isRegistering = useSelector(
    (state: StoreState) => state.extension.status === 'registering',
  );
  const isUnregistering = useSelector(
    (state: StoreState) => state.extension.isUnregistering,
  );
  const extension = useSelector((state: StoreState) => state.client.extension);
  const activeCustomer = useSelector(
    (state: StoreState) => state.client.activeCustomer,
  );
  const activeCustomerRef = useRef(activeCustomer);
  useEffect(() => {
    activeCustomerRef.current = activeCustomer;
  }, [activeCustomer]);
  const linkedUser = useSelector(
    (state: StoreState) => state.client.linkedUser,
  );
  const user = useSelector((state: StoreState) => state.user.user);
  const logAudioDiagnostics = shouldLogAudioDiagnostics(user, activeCustomer);
  const favicons = useSelector((state: StoreState) => state.branding.favicons);
  const calls = useSelector((state: StoreState) => state.calls);
  const apps = useSelector((state: StoreState) => state.branding.apps);
  const topBarInverted = useSelector(
    (state: StoreState) => state.branding.branding?.topbarInverted,
  );
  const kbLinkLocation = useSelector(
    (state: StoreState) => state.branding.kbLinkLocation,
  );
  const logoSrc = useSelector((state: StoreState) => state.branding.appLogoSrc);
  const selectedAudioInputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioInputDeviceId,
  );
  const selectedAudioOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioOutputDeviceId,
  );
  const selectedRingingOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedRingingOutputDeviceId,
  );
  const hasGrantedAudio = useSelector(
    (state: StoreState) => state.audio.hasGrantedAudio,
  );
  const showChangelogRequest = useSelector(
    (state: StoreState) => state.app.showChangelogRequest,
  );
  const lastOpenPreferencesRequest = useSelector(
    (state: StoreState) => state.audio.lastOpenPreferencesRequest,
  );
  const vcrmLicenceId = useSelector(
    (state: StoreState) => state.vcrm.licenceId,
  );
  const vcrmLaunchToken = useSelector(
    (state: StoreState) => state.vcrm.launchToken,
  );
  // const vcrmIsFetching = useSelector(
  //   (state: StoreState) => state.vcrm.isFetching,
  // );
  const vcrmConnecting = useSelector(
    (state: StoreState) => state.vcrm.isRegistering,
  );
  const vcrmConnected = useSelector(
    (state: StoreState) => state.vcrm.isRegistered,
  );

  useEffect(() => {
    setAudioDiagnosticsEnabled(logAudioDiagnostics);
  }, [logAudioDiagnostics]);

  useEffect(() => {
    if (!extension?.id || !isVcrmSupportedOs()) {
      return;
    }
    dispatch(fetchLicences({ extensionId: extension.id }));
  }, [dispatch, extension?.id]);

  useEffect(() => {
    if (!vcrmLicenceId || !isVcrmSupportedOs()) {
      return;
    }
    dispatch(fetchLaunchToken());
  }, [dispatch, vcrmLicenceId]);

  const icon32 = (
    (favicons &&
      favicons.find((fi) => fi.name.toLowerCase() === 'favicon-32x32.png')) ||
    {}
  ).location;

  const doRegisterExtension = useCallback(
    (
      username: string,
      password: string,
      domain: string,
      audio: UserAgentAudio,
    ) => {
      dispatch(registerExtension({ username, password, domain, audio }));
    },
    [dispatch],
  );
  const doLogout = () => {
    dispatch(logout());
  };
  const doGrantAudio = useCallback(
    (hasGrantedAudio: boolean) => {
      dispatch(grantAudio(hasGrantedAudio));
    },
    [dispatch],
  );

  const { info, close } = useNvAlert();

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [audioDeviceModalVisible, setAudioDeviceModalVisible] = useState(false);
  const [changelogVisible, setChangelogVisible] = useState(false);
  const [hasRegisteredOnce, setHasRegisteredOnce] = useState(false);
  const [canChangeAudioDevice] = useState(testCanChangeAudioDevice());
  const [requirePermissionsPrompt, setRequirePermissionsPrompt] =
    useState(false);
  const [autoplayFailed, setAutoplayFailed] = useState(false);

  const whatsNewToastIdRef = useRef<string>('');

  useEffect(() => {
    const STORAGE_KEY = 'nv-communicator-changelogLastSeenVersion';
    const stored = localStorage.getItem(STORAGE_KEY);
    localStorage.setItem(STORAGE_KEY, version);
    if (stored !== null && stored !== version) {
      const handleOpen = () => {
        close(whatsNewToastIdRef.current);
        setChangelogVisible(true);
      };
      whatsNewToastIdRef.current = info({
        title: `Updated to v${version}`,
        description: (
          <button
            onClick={handleOpen}
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              textDecoration: 'underline',
              font: 'inherit',
            }}
          >
            See what&apos;s new
          </button>
        ),
        timeout: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const prevPropsRef = useRef({
    extension,
    hasGrantedAudio,
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
    isRegistered,
    newOutboundCall,
  });
  const lastOpenPreferencesRequestRef = useRef<number>();
  const showChangelogRequestRef = useRef<number>();

  const checkAndRegisterExtension = useCallback(
    (reason: string, force?: boolean) => {
      // Sink is a module-level singleton (helpers/remoteAudio), not a ref, so
      // it survives remounts.
      const audio: UserAgentAudio = {
        get remote(): RemoteAudio | null {
          return getRemoteAudio();
        },
      };

      if (extension?.sip && !isRegistering && !isUnregistering) {
        if (!isRegistered || force) {
          const { username, password, domain } = extension.sip;
          console.debug(`Registering extension ${username}`);
          logActivity('default', 'REGISTER_EXTENSION', {
            reason,
            force: !!force,
          });
          doRegisterExtension(username, password, domain, audio);
        }
      }
    },
    [
      extension,
      isRegistering,
      isUnregistering,
      isRegistered,
      doRegisterExtension,
    ],
  );

  const getMedia = useCallback(() => {
    const constraints = {
      audio: true,
      video: false,
    };

    const requestUserMedia = () => {
      navigator.mediaDevices.getUserMedia(constraints).then(
        (stream) => {
          stream.getTracks().forEach((t) => t.stop());
          doGrantAudio(true);
          checkAndRegisterExtension('MEDIA_DEVICES');
        },
        (err) => {
          console.error(err);
        },
      );
    };

    // track changes in audio permissions
    // TODO this method is not supported in Firefox. Doesn't seem to
    // have any consequences, but double check just to be sure.
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((audioPermission) => {
        if (audioPermission.state === 'granted') {
          doGrantAudio(true);
          checkAndRegisterExtension('MEDIA_DEVICES');
        } else if (audioPermission.state === 'prompt') {
          requestUserMedia();
        }

        audioPermission.onchange = () => {
          if (audioPermission.state === 'granted') {
            doGrantAudio(true);
            checkAndRegisterExtension('MIC_PERMISSION');
          }
          if (audioPermission.state === 'denied') {
            doGrantAudio(false);
          }
          if (audioPermission.state === 'prompt') {
            doGrantAudio(false);
            setRequirePermissionsPrompt(true);
          }
        };
      })
      .catch(() => {
        requestUserMedia();
      });
  }, [checkAndRegisterExtension, doGrantAudio]);
  const setAudioDevices = useCallback(() => {
    if (canChangeAudioDevice) {
      const remoteAudio = getRemoteAudioElement();

      getAudioDevices({
        selectedAudioInputDeviceId,
        selectedAudioOutputDeviceId,
        selectedRingingOutputDeviceId,
      })
        .then(async (devices) => {
          const { selectedAudioOutputDevice } = devices;

          setRemoteOutputDeviceId(selectedAudioOutputDevice?.deviceId ?? '');
          if (selectedAudioOutputDevice && remoteAudio.srcObject) {
            try {
              await (remoteAudio as any).setSinkId?.(
                selectedAudioOutputDevice.deviceId,
              );
            } catch (e: any) {
              if (e?.name !== 'AbortError') console.error(e);
            }
          }
        })
        .catch(console.error);
    }
  }, [
    canChangeAudioDevice,
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
  ]);

  useEffect(() => {
    getMedia();

    dispatch(fetchOutgoingCallerIds());
    dispatch(fetchContacts());
    dispatch(fetchCallHistory({ overrideParams: undefined }));
    dispatch(fetchSmSMessages({}));
    dispatch(fetchTimeIntervals());

    const refreshLoop = setInterval(() => {
      if (!activeCustomerRef.current) {
        return;
      }
      dispatch(fetchContacts());
      dispatch(fetchNewCalls());
      dispatch(fetchSmSMessages({}));
      dispatch(fetchTimeIntervals());
    }, 60000);

    platform.requestNotificationPermissions();

    // check if autoplay is enabled
    const ctx = new AudioContext();
    ctx
      .resume()
      .then(() => {
        if (ctx.state !== 'running') setAutoplayFailed(true);
        ctx.close();
      })
      .catch(() => {
        console.debug('Autoplay failed');
        ctx.close();
        setAutoplayFailed(true);
      });

    return () => {
      if (refreshLoop) {
        clearInterval(refreshLoop);
      }
      partnerCrm.clearPartnerCrmPingInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAudioDevicesRef = useRef(setAudioDevices);
  useEffect(() => {
    setAudioDevicesRef.current = setAudioDevices;
  }, [setAudioDevices]);

  useEffect(() => {
    console.debug('Client useEffect', canChangeAudioDevice);

    if (canChangeAudioDevice) {
      setAudioDevices();
      navigator.mediaDevices.ondevicechange = () =>
        setAudioDevicesRef.current();
    }
  }, [canChangeAudioDevice, setAudioDevices]);

  const hideAll = () => {
    setShowLeft(false);
    setShowRight(false);
  };

  useEffect(() => {
    const thisExtn = extension;
    const prevExtn = prevPropsRef.current?.extension;

    let force = false;

    if (
      thisExtn?.sip &&
      prevExtn?.sip &&
      thisExtn.sip.username !== prevExtn.sip.username
    ) {
      logActivity('default', 'SIP_USERNAME_CHANGED', {
        new: thisExtn.sip.username,
        prev: prevExtn.sip.username,
      });
      force = true;
    }

    if (hasGrantedAudio && !prevPropsRef.current?.hasGrantedAudio) {
      checkAndRegisterExtension('HAS_GRANTED_AUDIO', force);
    }

    // -----------------------------------------

    if (
      hasRegisteredOnce &&
      !isRegistered &&
      prevPropsRef.current?.isRegistered
    ) {
      platform.notify(undefined, 'Communicator lost connection', '', {
        icon: icon32,
        timeout: 0,
      });
    }

    if (newOutboundCall && !prevPropsRef.current?.newOutboundCall) {
      hideAll();
    }

    if (!hasRegisteredOnce && isRegistered) {
      setHasRegisteredOnce(true);
    }

    if (
      lastOpenPreferencesRequest &&
      lastOpenPreferencesRequest !== lastOpenPreferencesRequestRef.current
    ) {
      lastOpenPreferencesRequestRef.current = lastOpenPreferencesRequest;
      setAudioDeviceModalVisible(true);
    }

    if (
      showChangelogRequest &&
      showChangelogRequest !== showChangelogRequestRef.current
    ) {
      showChangelogRequestRef.current = showChangelogRequest;
      setChangelogVisible(true);
    }

    prevPropsRef.current = {
      extension,
      hasGrantedAudio,
      selectedAudioInputDeviceId,
      selectedAudioOutputDeviceId,
      selectedRingingOutputDeviceId,
      isRegistered,
      newOutboundCall,
    };
  }, [
    extension,
    hasGrantedAudio,
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
    hasRegisteredOnce,
    isRegistered,
    newOutboundCall,
    lastOpenPreferencesRequest,
    showChangelogRequest,
    checkAndRegisterExtension,
    icon32,
  ]);

  const doShowLeft = () => {
    setShowLeft(true);
    setShowRight(false);
  };

  const doShowRight = () => {
    setShowLeft(false);
    setShowRight(true);
  };

  const handleCenterSwipe = (e: any) => {
    const LEFT = 2;
    const RIGHT = 4;
    const { direction } = e;

    if (direction === LEFT) {
      if (showLeft) {
        // Left is showing and we swipe it away
        hideAll();
      } else if (showRight) {
        // Right is showing and we swipe to open it again
        // Do nothing
      } else {
        // Both are hidden and we swipe left
        doShowRight();
      }
    } else if (direction === RIGHT) {
      if (showLeft) {
        // Left is showing and we swipe to open it again
        // Do nothing
      } else if (showRight) {
        // Right is showing and we swipe it away
        hideAll();
      } else {
        // Both are hidden and we swipe right
        // Show left
        doShowLeft();
      }
    }
  };

  const handleSidebarSwipe = (e: any) => {
    const LEFT = 2;
    const RIGHT = 4;
    const { direction } = e;

    if (showLeft && direction === LEFT) {
      // We're swiping the left drawer away
      hideAll();
    } else if (showRight && direction === RIGHT) {
      // We're swiping the right drawer away
      hideAll();
    }
  };

  const handleTap = (side: string) => {
    if (side === 'LEFT') {
      if (showLeft) {
        hideAll();
      } else {
        doShowLeft();
      }
    } else if (side === 'RIGHT') {
      if (showRight) {
        hideAll();
      } else {
        doShowRight();
      }
    }
  };

  const overlayTap = (e: any) => {
    e.preventDefault();
    hideAll();
  };

  const ringingCall = calls.find((c) => c.ringing && !c.forceStopRinging);
  const activeOngoingCall = calls.find((c) => c.active && c.answered);
  const ringingDirection =
    ringingCall && !activeOngoingCall ? ringingCall.direction : null;

  useEffect(() => {
    if (!ringingDirection) {
      peekOutboundRinging()?.pause();
      peekInboundRinging()?.pause();
      return;
    }
    const outboundRinging = getOutboundRinging();
    const inboundRinging = getInboundRinging();

    let active = true;
    const audio = ringingDirection === 'OUT' ? outboundRinging : inboundRinging;
    const ringSinkId =
      ringingDirection === 'OUT'
        ? selectedAudioOutputDeviceId
        : selectedRingingOutputDeviceId || selectedAudioOutputDeviceId;
    audio.setSinkId(ringSinkId || '').catch(console.error);

    audio
      .play()
      .then(() => {
        if (!active) return;
        if (logAudioDiagnostics) {
          Sentry.withScope((scope) => {
            scope.setLevel('info');
            scope.setTag('call_id', ringingCall?.id ?? 'unknown');
            scope.setTag('ringing_direction', ringingDirection ?? 'unknown');
            scope.setContext('audio', {
              direction: ringingDirection,
              sinkId: audio.sinkId,
            });
            Sentry.captureMessage('Ringing play() succeeded');
          });
        }
      })
      .catch((e) => {
        if (!active || !logAudioDiagnostics) return;
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('call_id', ringingCall?.id ?? 'unknown');
          scope.setTag('ringing_direction', ringingDirection ?? 'unknown');
          scope.setContext('audio', {
            direction: ringingDirection,
            sinkId: audio.sinkId,
          });
          Sentry.captureException(
            e instanceof Error
              ? e
              : new Error(`Ringing play() failed: ${String(e)}`),
          );
        });
      });

    return () => {
      active = false;
      audio.pause();
    };
  }, [
    ringingCall?.id,
    ringingDirection,
    logAudioDiagnostics,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
  ]);

  const vcrmMenuItems = useMemo(() => {
    if (!isVcrmSupportedOs()) {
      return [];
    }
    const menuItems: {
      label: string;
      onClick: () => void;
    }[] = [];
    if (vcrmLaunchToken && !vcrmConnected && !vcrmConnecting) {
      return [
        {
          label: 'Connect VertiCall CRM',
          onClick: () => {
            if (!vcrmLaunchToken) {
              return;
            }
            partnerCrm.launch(vcrmLaunchToken, activeCustomer?.country);
          },
        },
      ];
    }
    if (vcrmConnecting || vcrmConnected) {
      menuItems.push({
        label: 'Disconnect VertiCall CRM',
        onClick: () => {
          partnerCrm.unload();
        },
      });
    }
    if (vcrmConnected) {
      menuItems.push({
        label: 'Configure VertiCall CRM',
        onClick: () => {
          partnerCrm.showWindow();
        },
      });
    }
    return menuItems;
  }, [activeCustomer?.country, vcrmConnected, vcrmConnecting, vcrmLaunchToken]);

  if (!extension?.id) {
    return <ExtensionSelect />;
  }

  const userMenuItems = [
    {
      label: 'Log out',
      onClick: () => {
        doLogout();
      },
    },
  ];
  const settingsMenuItems = [];

  if (canChangeAudioDevice) {
    settingsMenuItems.push({
      label: 'Audio Settings',
      onClick: () => {
        setAudioDeviceModalVisible(true);
      },
    });
  }

  if (kbLinkLocation) {
    settingsMenuItems.push({
      label: 'Knowledge Base',
      href: kbLinkLocation,
      target: '_blank',
    });
  }

  settingsMenuItems.push({
    label: "What's New",
    onClick: () => setChangelogVisible(true),
  });

  const mappedApps = apps?.map((app) => {
    return {
      label: app.name,
      icon: app.icon,
      href: app.url,
    };
  });

  return (
    <>
      <div className={styles.container}>
        {isWeb() && (
          <NvTopbar
            className={styles.top_bar}
            customerName={activeCustomer?.company}
            product='Communicator'
            userEmail={linkedUser?.email}
            apps={isWeb() ? mappedApps : undefined}
            userMenuItems={userMenuItems}
            settingsMenuItems={settingsMenuItems}
            brandSrc={logoSrc}
            showBeta={false}
            inverted={topBarInverted}
            extraMenus={
              vcrmLaunchToken && isVcrmSupportedOs()
                ? [
                    {
                      key: 'vcrm',
                      icon: <VcrmIndicator />,
                      menuItems: vcrmMenuItems,
                    },
                  ]
                : undefined
            }
          />
        )}

        <div className={isWeb() ? styles.content : styles.contentFullHeight}>
          {showLeft || showRight ? (
            <Hammer onTap={overlayTap}>
              <div className={styles.close_area}>
                {/* Hammer requires exactly one child */}
              </div>
            </Hammer>
          ) : (
            ''
          )}

          <div className={showLeft ? styles.left_visible : styles.left_hidden}>
            <Hammer onSwipe={handleSidebarSwipe}>
              <div className={styles.left_sidebar_container}>
                <ContactList />
              </div>
            </Hammer>

            <Hammer
              onSwipe={() => handleTap('LEFT')}
              onTap={() => handleTap('LEFT')}
            >
              <div
                className={
                  showLeft ? styles.left_handle_open : styles.left_handle_closed
                }
              >
                <ChevronRightIcon />
              </div>
            </Hammer>
          </div>

          <div className={styles.column__middle}>
            {!hasGrantedAudio && (
              <span className={styles.audio_permissions_denied}>
                {requirePermissionsPrompt
                  ? 'Permissions have been reset, please refresh the page for new settings to take effect'
                  : `Looks like we don't have access to your microphone. You won't be able to make or receive calls until this is enabled.`}
              </span>
            )}
            <Hammer onSwipe={handleCenterSwipe}>
              <div className={styles.center_column}>
                <CenterColumn />
              </div>
            </Hammer>
          </div>

          <div
            className={showRight ? styles.right_visible : styles.right_hidden}
          >
            <Hammer onSwipe={handleSidebarSwipe}>
              <div className={styles.right_sidebar_container}>
                <RightPane />
              </div>
            </Hammer>

            <Hammer
              onSwipe={() => handleTap('RIGHT')}
              onTap={() => handleTap('RIGHT')}
            >
              <div
                className={
                  showRight
                    ? styles.right_handle_open
                    : styles.right_handle_closed
                }
              >
                <ChevronLeftIcon />
              </div>
            </Hammer>
          </div>

          {hasGrantedAudio &&
            canChangeAudioDevice &&
            audioDeviceModalVisible && (
              <AudioDeviceModal
                onCancel={() => {
                  setAudioDeviceModalVisible(false);
                }}
                onSave={() => {
                  setAudioDeviceModalVisible(false);
                }}
              />
            )}
          {changelogVisible && (
            <ChangelogModal
              content={changelogContent}
              onClose={() => setChangelogVisible(false)}
            />
          )}
          {visibleExtensionMenu === 'extension' && (
            <ExtensionSettingsModal extension={extension} />
          )}
          {visibleExtensionMenu === 'voicemail' && (
            <VoicemailSettingsModal extension={extension} />
          )}
          {visibleExtensionMenu === 'forwardingRules' && (
            <ForwardingRulesModal extension={extension} />
          )}
        </div>

        {hasRegisteredOnce && !isRegistered && (
          <Popover
            title='Connection Lost'
            message="Looks like we're having some trouble connecting, hang on a moment and we'll keep trying."
          />
        )}
      </div>
      {autoplayFailed && (
        <div className={styles.autoplayModalContainer}>
          <div className={styles.autoplayModal}>
            <span>
              Please close this modal to enable ringing sound to be played
            </span>
            <div
              className={styles.autoplayModalCloseButton}
              onClick={() => {
                getInboundRinging().resume();
                getOutboundRinging().resume();
                getRemoteAudioElement().play().catch(console.warn);
                setAutoplayFailed(false);
              }}
              onKeyDown={handleEnterPress(() => {
                getInboundRinging().resume();
                getOutboundRinging().resume();
                getRemoteAudioElement().play().catch(console.warn);
                setAutoplayFailed(false);
              })}
              role='button'
              tabIndex={0}
            >
              <ClearIcon size='1.3em' />
            </div>
          </div>
        </div>
      )}
      {/* Remote call-audio sink is a singleton in helpers/remoteAudio, not
          rendered here (must outlive remounts). */}
    </>
  );
};

export default Client;
